// src/components/features/PathfinderModules/SpecialtyBox.js
import React, { useState, useEffect } from 'react';
import { 
  FaExpandAlt, 
  FaTimes, 
  FaStethoscope,
} from 'react-icons/fa';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { listUserProfiles } from '../../../graphql/queries';
import { updateTrainingPathway } from '../../../api/trainingPathwayApi';
import specialtiesData from '../../../data/SpecialtyOverview.json';

const SpecialtyBox = ({
  expandedContainer,
  onExpand,
  onClose,
  isExpandedInCenter,
  onTrainingPathChange, // Props for handling training path changes
  initialTrainingPath = 'IMT' // New prop for initial training path
}) => {
  const isExpanded = expandedContainer === 'specialtyBox' || isExpandedInCenter;
  const [selectedTrainingPath, setSelectedTrainingPath] = useState(initialTrainingPath);
  const [userSpecialty, setUserSpecialty] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Update selectedTrainingPath when initialTrainingPath changes
  useEffect(() => {
    if (initialTrainingPath && initialTrainingPath !== selectedTrainingPath) {
      console.log(`SpecialtyBox updating training path from props: ${initialTrainingPath}`);
      setSelectedTrainingPath(initialTrainingPath);
    }
  }, [initialTrainingPath]);

  // Fetch user profile and chosen specialty
  useEffect(() => {
    const fetchUserSpecialty = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current user
        const user = await getCurrentUser();
        if (!user) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }
        
        const client = generateClient();
        // Get user profile
        const { data } = await client.graphql({
          query: listUserProfiles,
          variables: {
            filter: { owner: { eq: user.username } }
          },
          authMode: 'userPool'
        });
        
        if (data.listUserProfiles.items.length > 0) {
          const profile = data.listUserProfiles.items[0];
          setUserProfile(profile);
          
          if (profile.chosenSpecialty) {
            setUserSpecialty(profile.chosenSpecialty);
          }
        }
      } catch (err) {
        console.error('Error fetching user specialty:', err);
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserSpecialty();
  }, []);
  
  // Handle training path change
  const handleTrainingPathChange = async (path) => {
    setSelectedTrainingPath(path);
    
    // Notify parent component about the training path change
    if (onTrainingPathChange) {
      onTrainingPathChange(path);
    }
    
    // Save the training path to the backend if we have a user profile
    if (userProfile) {
      try {
        await updateTrainingPathway(userProfile.id, path);
        console.log(`Training path updated to ${path}`);
      } catch (err) {
        console.error('Error saving training path:', err);
      }
    }
  };

  const renderCompactView = () => (
    <>
      <h4>Your Specialty</h4>
      {error ? (
        <p className="error-message">{error}</p>
      ) : (
        <p>{userSpecialty || "Select a specialty"}</p>
      )}
      
      {/* Training path selector with ACCS option HIDDEN */}
      {/* <div className="training-path-selector">
        <label htmlFor="training-path">Training Path:</label>
        <select 
          id="training-path" 
          value={selectedTrainingPath}
          onChange={(e) => handleTrainingPathChange(e.target.value)}
          className="training-path-dropdown"
        >
          <option value="ACCS">Acute Care Common Stem (ACCS)</option>
          <option value="IMT">Internal Medicine Training (IMT)</option>
          <option value="CST">Core Surgical Training (CST)</option>
          <option value="Neurosurgery">Neurosurgery (ST1)</option>
          <option value="Radiology">Radiology (ST1)</option>
        </select>
      </div> */}
      
      <div className="compact-stats">
        <div className="stat">
          
          <span>4 years remaining</span>
        </div>
        <div className="stat">
          
          <span>On track</span>
        </div>
      </div>
    </>
  );

  const renderExpandedView = () => (
    <div className="expanded-content">
      <h2>{userSpecialty || 'Specialty'} Training Path</h2>
      <div className="specialty-overview">
        <div className="overview-header">
          <FaStethoscope className="specialty-icon" />
          <div>
            <h3>{userSpecialty || 'No Specialty Selected'}</h3>
            <p className="subtitle">
              {userSpecialty
                ? 'Your currently selected goal specialty.'
                : 'Choose a specialty to begin tracking.'}
            </p>
          </div>
        </div>
        {/* All former dummy timeline / metrics / requirements / path selectors removed */}
      </div>
    </div>
  );

  return (
    <div className={`info-box specialty-box ${isExpandedInCenter ? 'center-expanded' : ''}`}>
      {!isExpandedInCenter && (
        isExpanded ? (
          <button className="control-btn close" onClick={onClose} title="Close">
            <FaTimes size={12} />
          </button>
        ) : (
          <button className="control-btn expand" onClick={() => onExpand('specialtyBox')} title="Expand">
            <FaExpandAlt size={12} />
          </button>
        )
      )}

      {isExpandedInCenter ? renderExpandedView() : renderCompactView()}

      <style jsx>{`
        /* Core container */
        .specialty-box {
          /* Keep styles here but they'll be overridden by the global styles when in sidebar */
        }

        .specialty-box.center-expanded {
          background: #fff;
          border-radius: 12px;
          padding: 1.5rem;
          position: relative;
        }

        .expanded-content {
          padding: 2rem 3rem;
        }

        .expanded-content h2 {
          text-align: center;
          color: #1e293b;
          font-size: 1.875rem;
          font-weight: 700;
          margin-bottom: 2.5rem;
          letter-spacing: -0.025em;
        }

        .specialty-overview {
          max-width: 640px;
          margin: 0 auto;
        }

        .overview-header {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        @media (max-width: 640px) {
          .expanded-content { padding: 1.5rem; }
          .expanded-content h2 { 
            font-size: 1.5rem; 
            margin-bottom: 1.5rem;
          }
          .overview-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }
        }

        .specialty-icon {
          font-size: 2.5rem;
          color: #3b82f6;
          padding: 1rem;
          background: #eff6ff;
          border-radius: 12px;
        }

        .overview-header h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .subtitle {
            color: #64748b;
            margin: 0.25rem 0 0 0;
        }

        /* Compact view + existing styles (unchanged) */
        .compact-stats { display: flex; gap: 1rem; margin-top: 1rem; }
        .stat { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #64748b; }
        .stat-icon { color: #3b82f6; }

        .control-btn {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          padding: 0.5rem;
          border: none;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 6px;
        }
        .control-btn:hover {
          background: #f1f5f9;
          color: #64748b;
        }

        .training-path-selector {
          margin: 1rem 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .training-path-selector label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #64748b;
        }
        .training-path-dropdown {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #e2e8f0;
          border-radius: 0.375rem;
          background-color: #f8fafc;
          color: #1e293b;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        .training-path-dropdown:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .error-message {
          color: #ef4444;
          text-align: center;
          padding: 0.5rem;
          margin: 0.5rem 0;
          background-color: #fee2e2;
          border-radius: 0.375rem;
          border: 1px solid #fecaca;
        }
        .error-message.centered {
          margin: 2rem auto;
          max-width: 80%;
        }
      `}</style>
    </div>
  );
};

export default SpecialtyBox;